import {
  FilterActions,
  FilterLabels,
  MainFilterSelectionEvent,
  QuickFilterSelectionEvent,
  RESET_FILTER_SELECTION,
} from '../../../types';
import {
  DetailedPageInfo,
  EventPageData,
  InitialPageInfo,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {stringifyEventData} from '../helpers/stringifyEventData';

export type InternalMainFilterSelectionEvent = MainFilterSelectionEvent & {
  type: 'main';
};

type InternalQuickFilterSelectionEvent = QuickFilterSelectionEvent & {
  type: 'quick';
};

export type InternalFilterSelectionEvent =
  | InternalMainFilterSelectionEvent
  | InternalQuickFilterSelectionEvent;

export interface DataLayerQuickFilterEvent {
  GA4: DataLayerQuickFilterEventGA4;
}
export type DataLayerQuickFilterEventGA3 = {
  event: 'filter';
  /**
   * "filterSlug" e.g. 'none' | 'forYou' | 'new' | 'sale' | 'styleGroup'
   */
  action: string;
  /**
   * selected values 'selectedValue'
   */
  label: string;
  value: null;
  attribute_group?: string;
  attributes?: string;
};

export type DataLayerQuickFilterEventGA4 = EventPageData & {
  event: 'filter';
  /**
   * "filterSlug" e.g. 'none' | 'forYou' | 'new' | 'sale' | 'styleGroup'
   */
  action: string;
  /**
   * selected values 'selectedValue'
   */
  label: string;
  value: null;
  attribute_group?: string;
  attributes?: string;
};

export interface DataLayerMainFilterEvent {
  GA3: DataLayerMainFilterEventGA3;
  GA4: DataLayerMainFilterEventGA4;
}

type DataLayerMainFilterEventGA3 = {
  event: 'filter';
  /**
   * "filterSlug"
   */
  action: string;
  /**
   * "val0|val1|val2|..." || "min|max" || "true" || "false"
   */
  label: string;
  value: null;
  attribute_group?: string;
  attributes?: string;
};

type DataLayerMainFilterEventGA4 = EventPageData & {
  event: 'filter';
  /**
   * "filterSlug"
   */
  action: string;
  /**
   * "val0|val1|val2|..." || "min|max" || "true" || "false"
   */
  label: string;
  value: null;
  attribute_group?: string;
  attributes?: string;
};

export interface DataLayerResetFilterEvent {
  GA4: DataLayerResetFilterEventGA4;
}

type DataLayerResetFilterEventGA4 = EventPageData & {
  event: 'filter';
  filter_name: 'reset';
  filter_value: null;
  value: null;
};
export type FilterDataLayerEvent =
  | DataLayerQuickFilterEvent
  | DataLayerMainFilterEvent
  | DataLayerResetFilterEvent;

export const trackFilterSelection =
  ({
    selection,
    action,
    label,
  }: {
    selection: InternalFilterSelectionEvent | typeof RESET_FILTER_SELECTION;
    action: FilterActions;
    label: FilterLabels;
  }) =>
  (clientState: TrackingClientStateHolder) => {
    const {currentPage, selectedFilters} = clientState.getCurrent();
    if (selection === RESET_FILTER_SELECTION) {
      clientState.update({
        selectedFilters: {},
      });

      const eventGA4: DataLayerResetFilterEventGA4 = {
        event: 'filter',
        filter_name: 'reset',
        filter_value: null,
        value: null,
        content_name: currentPage?.page?.path || '',
        page_type: currentPage?.page?.type || '',
        page_type_id:
          currentPage?.type === 'detailed'
            ? `${currentPage.page?.id || ''}`
            : '',
      };

      return {
        GA4: eventGA4,
      };
    }

    const event = convertToDataLayerEvent(
      selection,
      action,
      label,
      currentPage
    );
    clientState.update({
      selectedFilters: {
        ...selectedFilters,
        [selection.type === 'quick' ? '__quickFilter' : selection.slug]:
          selection,
      },
    });

    return event;
  };

function isQuickFilterEvent(
  e: InternalFilterSelectionEvent
): e is InternalQuickFilterSelectionEvent {
  return e.type === 'quick';
}

function convertToDataLayerEvent(
  e: InternalFilterSelectionEvent,
  action: FilterActions,
  label: FilterLabels,
  currentPage?: InitialPageInfo | DetailedPageInfo
): DataLayerQuickFilterEvent | DataLayerMainFilterEvent {
  if (isQuickFilterEvent(e)) {
    const eventGA3: DataLayerQuickFilterEventGA3 = {
      event: 'filter',
      action,
      label,
      value: null,
      attribute_group: stringifyEventData([e.slug]),
      attributes: stringifyEventData([e.selectedValue]),
    };
    const eventGA4: DataLayerQuickFilterEventGA4 = {
      event: 'filter',
      action,
      label,
      value: null,
      content_name: currentPage?.page?.path || '',
      page_type: currentPage?.page?.type || '',
      page_type_id:
        currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
      attribute_group: stringifyEventData([e.slug]),
      attributes: stringifyEventData([e.selectedValue]),
    };
    const event = {
      GA3: eventGA3,
      GA4: eventGA4,
    };
    return event;
  } else {
    const eventGA3: DataLayerMainFilterEventGA3 = {
      event: 'filter',
      action,
      label,
      value: null,
      attribute_group: stringifyEventData([e.slug]),
      attributes:
        e.filterType === 'attribute' || e.filterType === 'range'
          ? stringifyEventData(e.selectedValue)
          : stringifyEventData([e.selectedValue]),
    };

    const eventGA4: DataLayerMainFilterEventGA4 = {
      event: 'filter',
      action,
      label,
      value: null,
      content_name: currentPage?.page?.path || '',
      page_type: currentPage?.page?.type || '',
      page_type_id:
        currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
      attribute_group: stringifyEventData([e.slug]),
      attributes:
        e.filterType === 'attribute' || e.filterType === 'range'
          ? stringifyEventData(e.selectedValue)
          : stringifyEventData([e.selectedValue]),
    };

    const event = {
      GA3: eventGA3,
      GA4: eventGA4,
    };
    return event;
  }
}
